import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import preguntaService from '../../services/preguntaService';
import examenService from '../../services/examenService';

export default function Preguntas() {
  const [loading, setLoading] = useState(true);
  const [preguntas, setPreguntas] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar exámenes primero
      let examenesData = [];
      try {
        examenesData = await examenService.getAllExamenes();
        setExamenes(examenesData || []);
      } catch (examenError) {
        console.error('Error cargando exámenes:', examenError);
        if (examenError.response?.status !== 400) {
          toast.error('Error al cargar exámenes');
        }
        setExamenes([]);
      }

      // Cargar preguntas
      let preguntasData = [];
      try {
        preguntasData = await preguntaService.getAllPreguntas();
        setPreguntas(preguntasData || []);
      } catch (preguntaError) {
        console.error('Error cargando preguntas:', preguntaError);
        if (preguntaError.response?.status !== 400) {
          toast.error('Error al cargar preguntas');
        }
        setPreguntas([]);
      }
    } catch (error) {
      console.error('Error general:', error);
      setPreguntas([]);
      setExamenes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      const payload = {
        texto: data.texto,
        tipo: data.tipo,
        peso: parseFloat(data.peso),
        examenId: parseInt(data.examenId)
      };

      if (editingItem) {
        await preguntaService.updatePregunta(editingItem.id, payload);
        toast.success('Pregunta actualizada');
      } else {
        await preguntaService.createPregunta(payload);
        toast.success('Pregunta creada');
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta pregunta?')) return;
    
    try {
      await preguntaService.deletePregunta(id);
      toast.success('Pregunta eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const filteredPreguntas = preguntas.filter(p =>
    p.texto?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Preguntas</h1>
          <p className="text-neutral-600 mt-1">Gestión de preguntas de examen</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Pregunta
        </Button>
      </div>

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar preguntas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Texto</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Tipo</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Peso</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Examen</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredPreguntas.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-neutral-500">
                  No se encontraron preguntas
                </td>
              </tr>
            ) : (
              filteredPreguntas.map((pregunta) => (
                <tr key={pregunta.id} className="hover:bg-neutral-50">
                  <td className="p-4 font-medium text-neutral-800 max-w-md truncate">
                    {pregunta.texto}
                  </td>
                  <td className="p-4 text-neutral-600">{pregunta.tipo}</td>
                  <td className="p-4 text-neutral-600">{pregunta.peso}</td>
                  <td className="p-4 text-neutral-600">{pregunta.examenNombre || pregunta.examen?.nombre || 'N/A'}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(pregunta)}
                        className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(pregunta.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PreguntaModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        examenes={examenes}
      />
    </div>
  );
}

function PreguntaModal({ isOpen, onClose, onSave, initialData, examenes }) {
  const [formData, setFormData] = useState({
    texto: '',
    tipo: 'opcion_multiple',
    peso: '',
    examenId: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        texto: initialData.texto || '',
        tipo: initialData.tipo || 'opcion_multiple',
        peso: initialData.peso || '',
        examenId: initialData.examenId || initialData.examen?.id || ''
      });
    } else {
      setFormData({
        texto: '',
        tipo: 'opcion_multiple',
        peso: '',
        examenId: ''
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Pregunta' : 'Nueva Pregunta'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Texto de la pregunta
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            value={formData.texto}
            onChange={(e) => setFormData({ ...formData, texto: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Tipo
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.tipo}
            onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
            required
          >
            <option value="opcion_multiple">Opción Múltiple</option>
            <option value="verdadero_falso">Verdadero/Falso</option>
            <option value="abierta">Abierta</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Examen
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.examenId}
            onChange={(e) => setFormData({ ...formData, examenId: e.target.value })}
            required
          >
            <option value="">Seleccionar examen</option>
            {examenes.map(e => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </div>
        <Input
          label="Peso"
          type="number"
          step="0.1"
          value={formData.peso}
          onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
          required
        />
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
